import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { api } from "../../api";
import { fetchBatchesCompleteness } from "../../functions/fetchBatchesCompleteness";
import { IncompleteScheduleWarning } from "../IncompleteScheduleWarning";
import LoadingText from "../LoadingText";
import { Badge } from "../tailwind/badge";
import { Divider } from "../tailwind/divider";
import { Text } from "../tailwind/text";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../tailwind/table";
import { useFindMany } from "@gadgetinc/react";
import { ChevronRightIcon } from "@heroicons/react/16/solid";
import { formatDateTime } from "../../functions/date-utils";
import { useNavigate } from "react-router";
import { Link } from "../tailwind/link";

export function ViewScheduledCampaigns({ customerId, className, isMobile }) {
  const navigate = useNavigate();
  const [{ data: scheduledCampaigns, fetching }] = useFindMany(api.campaign, {
    filter: {
      customerId: { equals: customerId },
      isArchived: { notEquals: true },
      trigger: { equals: "manual" },
      sendings: {
        some: {
          status: { equals: "registered" },
        },
      },
    },
    sort: {
      updatedAt: "Descending",
    },
    select: {
      id: true,
      name: true,
      scheduleDate: true,
      scheduledAudience: true,
    },
    first: 10,
  });

  const nextScheduledCampaigns = useMemo(() => {
    return (scheduledCampaigns ?? [])
      .filter((campaign) => campaign.scheduleDate)
      .sort((a, b) => new Date(a.scheduleDate) - new Date(b.scheduleDate))
      .slice(0, 3);
  }, [scheduledCampaigns]);

  const [completeness, setCompleteness] = useState(new Map());
  useEffect(() => {
    const batchIds = [
      ...new Set(
        nextScheduledCampaigns.flatMap((campaign) =>
          campaign.scheduledAudience.map((s) => s.sendingBatchId),
        ),
      ),
    ].filter(Boolean);
    fetchBatchesCompleteness(customerId, batchIds)
      .then(setCompleteness)
      .catch((error) =>
        console.error("Error fetching batches completeness:", error),
      );
  }, [customerId, nextScheduledCampaigns]);

  return (
    nextScheduledCampaigns.length > 0 && (
      <div className={clsx(className, "border rounded-xl p-3 shadow bg-white")}>
        <Link onClick={() => navigate("/campaigns/sendings")}>
          <div className="flex items-center hover:underline hover:cursor-pointer">
            <Text className="text-lg font-bold mb-2">Scheduled campaigns</Text>
            <ChevronRightIcon className="h-6 w-6 mb-1" />
          </div>
        </Link>
        <Divider />

        <Table dense>
          <TableHead>
            <TableRow>
              {!isMobile && <TableHeader>Status</TableHeader>}
              <TableHeader>Campaign</TableHeader>
              <TableHeader>Next sending</TableHeader>
              <TableHeader className="text-center">Audience</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {fetching ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <LoadingText className="my-3 h-[24px]" />
                </TableCell>
              </TableRow>
            ) : nextScheduledCampaigns.length > 0 ? (
              nextScheduledCampaigns.map((campaign) => (
                <CampaignRow
                  key={campaign.id}
                  campaign={campaign}
                  completeness={completeness}
                  isMobile={isMobile}
                  navigate={navigate}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7}>
                  <Text className="text-center py-4 text-lg text-gray-700">
                    Start sending campaigns 📢
                  </Text>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    )
  );
}

function CampaignRow({ campaign, completeness, isMobile }) {
  const audience = campaign.scheduledAudience.reduce((total, item) => {
    return total + (item.contactList?.length || 0);
  }, 0);
  const incompleteBatches = [
    ...new Set(campaign.scheduledAudience.map((s) => s.sendingBatchId)),
  ]
    .map((batchId) => completeness.get(batchId))
    .filter((batch) => batch?.isIncomplete);
  return (
    <TableRow
      key={campaign.id}
      href={`/campaigns/edit/${campaign.id}`}
      className="group hover:bg-muted transition"
    >
      {!isMobile && (
        <TableCell>
          <Badge color="violet">Scheduled</Badge>
        </TableCell>
      )}
      <TableCell>{campaign.name}</TableCell>
      <TableCell>
        {campaign.scheduleDate
          ? isMobile
            ? formatDateTime(new Date(campaign.scheduleDate), "shortDate")
            : formatDateTime(
                new Date(campaign.scheduleDate),
                "shortTimeMediumDate",
              )
          : "-"}
      </TableCell>
      <TableCell className="text-center">
        {audience ?? "-"}
        {incompleteBatches.length > 0 && (
          <IncompleteScheduleWarning
            scheduledContacts={incompleteBatches.reduce(
              (sum, b) => sum + b.scheduledContacts,
              0,
            )}
            plannedContacts={incompleteBatches.reduce(
              (sum, b) => sum + b.plannedContacts,
              0,
            )}
          />
        )}
      </TableCell>
    </TableRow>
  );
}
